import { useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Badge,
  Divider,
  Paper,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconTransfer,
  IconAlertTriangle,
  IconClock,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api, type ActiveHandover } from '../lib/api';

interface HandoverBannerProps {
  handover: ActiveHandover;
  /** Used only for cache invalidation — CW: number, Weclapp: string */
  ticketId: number | string;
}

export function HandoverBanner({ handover, ticketId }: HandoverBannerProps) {
  const queryClient = useQueryClient();
  const [respondModalOpen, setRespondModalOpen] = useState(false);
  const [respondAction, setRespondAction] = useState<'accept' | 'decline' | null>(null);
  const [responseNote, setResponseNote] = useState('');

  function invalidateTicketQueries() {
    if (handover.source === 'WECLAPP') {
      queryClient.invalidateQueries({ queryKey: ['weclapp-handover-active', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['weclapp-ticket', ticketId] });
      // Der Vermerk zur Übergabe steht schon im Kommentar-Spiegel — ohne Neuladen fehlte er im Verlauf.
      queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', String(ticketId)] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['ticket-handover-active', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      // Annahme überträgt den Owner – Cockpit-Header & Board-/Ticket-Listen müssen das sofort spiegeln
      queryClient.invalidateQueries({ queryKey: ['ticket-cockpit', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-all-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['my-kanban-tickets-all'] });
      queryClient.invalidateQueries({ queryKey: ['team-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['pending-claims-on-my-tickets'] });
    }
  }

  const { mutate: respond, isPending } = useMutation({
    mutationFn: (action: 'accept' | 'decline') =>
      api.respondToHandover(handover.id, { action, responseNote: responseNote.trim() || undefined }),
    onSuccess: (_data, action) => {
      notifications.show({
        title: action === 'accept' ? 'Übergabe angenommen' : 'Übergabe abgelehnt',
        message:
          action === 'accept'
            ? 'Das Ticket wurde auf dich übertragen. Die Begründung wurde als interner Kommentar gespeichert.'
            : 'Die Übergabe wurde abgelehnt. Das Ticket verbleibt beim bisherigen Techniker.',
        color: action === 'accept' ? 'green' : 'red',
      });
      invalidateTicketQueries();
      setRespondModalOpen(false);
    },
    onError: (err: any) => {
      const message = err?.body?.error ?? err?.message ?? 'Unbekannter Fehler';
      notifications.show({ title: 'Fehler', message, color: 'red' });
    },
  });

  const { mutate: cancel, isPending: isCancelling } = useMutation({
    mutationFn: () => api.cancelHandover(handover.id),
    onSuccess: () => {
      notifications.show({
        title: 'Übergabe abgebrochen',
        message: 'Die Übergabe wurde zurückgezogen.',
        color: 'gray',
      });
      invalidateTicketQueries();
    },
    onError: (err: any) => {
      const message = err?.body?.error ?? err?.message ?? 'Unbekannter Fehler';
      notifications.show({ title: 'Fehler', message, color: 'red' });
    },
  });

  function openRespond(action: 'accept' | 'decline') {
    setRespondAction(action);
    setResponseNote('');
    setRespondModalOpen(true);
  }

  const createdAt = new Date(handover.createdAt).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <>
      {/* Banner für den Empfänger */}
      {handover.isRecipient && (
        <Alert
          icon={<IconTransfer size={18} />}
          title="Ticket-Übergabe ausstehend"
          color="orange"
          variant="light"
          mb="md"
        >
          <Stack gap="sm">
            <Text size="sm">
              <strong>{handover.fromUser.displayName}</strong> möchte dir dieses Ticket übergeben.
            </Text>

            <Paper withBorder p="sm" radius="sm" style={{ background: 'var(--mantine-color-default)' }}>
              <Text size="xs" c="dimmed" fw={600} mb={4}>Begründung der Übergabe</Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{handover.reason}</Text>
            </Paper>

            <Stack gap={4}>
              <Group gap="xs">
                <IconCheck size={14} color="var(--mantine-color-green-6)" />
                <Text size="xs" c="dimmed">Persönliches Gespräch wurde bestätigt</Text>
              </Group>
              <Group gap="xs">
                <IconCheck size={14} color="var(--mantine-color-green-6)" />
                <Text size="xs" c="dimmed">Infos sind schriftlich im Ticket dokumentiert</Text>
              </Group>
            </Stack>

            <Group gap="xs" justify="flex-end">
              <Button
                size="xs"
                variant="filled"
                color="red"
                leftSection={<IconX size={14} />}
                onClick={() => openRespond('decline')}
                disabled={isPending || isCancelling}
              >
                Ablehnen
              </Button>
              <Button
                size="xs"
                variant="filled"
                color="green"
                leftSection={<IconCheck size={14} />}
                onClick={() => openRespond('accept')}
                disabled={isPending || isCancelling}
              >
                Übergabe annehmen
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {/* Banner für den Initiator */}
      {handover.isInitiator && (
        <Alert
          icon={<IconClock size={18} />}
          title="Übergabe wartet auf Bestätigung"
          color="blue"
          variant="light"
          mb="md"
        >
          <Stack gap="sm">
            <Text size="sm">
              Du hast dieses Ticket an <strong>{handover.toUser.displayName}</strong> übergeben.
              Warte auf die Bestätigung — das Ticket ist noch nicht übertragen.
            </Text>
            <Group gap="xs">
              <Badge variant="dot" color="blue" size="sm">Ausstehend seit {createdAt}</Badge>
            </Group>
            <Group justify="flex-end">
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                leftSection={<IconX size={14} />}
                onClick={() => cancel()}
                loading={isCancelling}
                disabled={isPending}
              >
                Übergabe abbrechen
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {/* Antwort-Modal */}
      <Modal
        opened={respondModalOpen}
        onClose={() => !isPending && setRespondModalOpen(false)}
        title={
          <Group gap="xs">
            {respondAction === 'accept'
              ? <IconCheck size={18} color="var(--mantine-color-green-6)" />
              : <IconAlertTriangle size={18} color="var(--mantine-color-red-6)" />
            }
            <Text fw={600}>
              {respondAction === 'accept' ? 'Übergabe annehmen' : 'Übergabe ablehnen'}
            </Text>
          </Group>
        }
        size="md"
        closeOnClickOutside={!isPending}
        closeOnEscape={!isPending}
        zIndex={1300}
      >
        <Stack gap="md">
          <Paper withBorder p="sm" radius="sm">
            <Text size="xs" c="dimmed" fw={600} mb={4}>Ursprüngliche Begründung von {handover.fromUser.displayName}</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{handover.reason}</Text>
          </Paper>

          <Divider />

          <Textarea
            label={
              respondAction === 'accept'
                ? 'Rückmeldung / Ergänzung (optional)'
                : 'Ablehnungsgrund (optional, aber empfohlen)'
            }
            description={
              respondAction === 'accept'
                ? 'Du kannst die Begründung ergänzen oder eine Anmerkung hinterlassen. Wird als interner Kommentar gespeichert.'
                : 'Erkläre kurz, warum du die Übergabe ablehnst. Wird als interner Kommentar gespeichert.'
            }
            placeholder={
              respondAction === 'accept'
                ? 'z. B. Alles klar, ich übernehme das Ticket.'
                : 'z. B. Ich bin diese Woche ebenfalls nicht verfügbar.'
            }
            value={responseNote}
            onChange={(e) => setResponseNote(e.currentTarget.value)}
            minRows={3}
            maxRows={6}
            autosize
            disabled={isPending}
          />

          {respondAction === 'accept' && (
            <Alert icon={<IconCheck size={16} />} color="green" variant="light">
              <Text size="sm">
                Das Ticket wird nach der Bestätigung offiziell auf dich übertragen.
                Beide Begründungen werden als interne Kommentare gespeichert.
              </Text>
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setRespondModalOpen(false)}
              disabled={isPending}
            >
              Zurück
            </Button>
            <Button
              color={respondAction === 'accept' ? 'green' : 'red'}
              leftSection={respondAction === 'accept' ? <IconCheck size={16} /> : <IconX size={16} />}
              loading={isPending}
              onClick={() => respondAction && respond(respondAction)}
            >
              {respondAction === 'accept' ? 'Übergabe bestätigen' : 'Ablehnen bestätigen'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
